import { Alert, Box, Button, FormGroup, FormSection, Heading, NumberInput, Switch, Text } from '@ksyos/design-system';
import { chain, findKey, flatten, set } from 'lodash';
import { Fragment } from 'react';
import type { IAddCarePathServiceActivities } from '@common/interfaces';
import type { settingDefinitionList } from '@common/utils/settings';
import type { ServiceCode } from '../../../../../common/enums/serviceCode';
import type {
    IAddCarePathSettings,
    TValueOfAddCarePathSettings,
} from '../../../../../common/interfaces/addCarePathSettings';
import type { IEpisodeCategoryBasic } from '../../../../../common/interfaces/episodeCategory';
import type { ValidationErrors } from '../../../../utils/validation';
import type { IServiceEmploymentSetting } from '../../personalSettings/addRights/utils/serviceEmploymentSettingsUtils';
import { ServiceType } from '../../../../../common/enums/serviceType';
import {
    compareByNestedServiceTypeSortOrderAndCode,
    compareByServiceCodeSortOrder,
} from '../../../../../common/utils/serviceSortUtils';
import { translateKey } from '../../../../translate';
import { getValidationErrorTranslation } from '../../../../utils/getValidationErrorTranslation';
import { Modal } from '../../../elements/modals/Modal';
import { DropdownInput } from '../../../fields/Dropdown';
import { generateOptions } from '../../customControls/utils/v1/generateOptions';
import { possiblyEmptyServiceTypes } from '../../organizations/organizationAddCarePath/constants/possiblyEmptyServiceTypes';
import { getAddCarePathServiceEmploymentSettingsList } from '../constants/serviceEmploymentSettingsList';
import { SettingAnswerTypes, settingsList } from '../constants/settingsLists';
import { SettingsCanWrite } from './components/SettingsCanWrite';
import { SettingsSwitch } from './components/SettingsSwitch';
import { SubsequentServices } from './components/SubsequentServices';

interface ICarePathSettingsModalProps {
    employmentStartDate?: string;
    episodeCategory: IEpisodeCategoryBasic;
    errors: ValidationErrors;
    onCancel: () => void;
    onChange: (settings: IAddCarePathSettings, errorPath: string) => void;
    onSave: () => void;
    settings: IAddCarePathSettings;
}

export function CarePathSettingsModal(props: ICarePathSettingsModalProps) {
    const flattenedSettings = flatten(Object.values(props.settings));
    const allSettingsAreDisabled = flattenedSettings.every(
        (setting: TValueOfAddCarePathSettings) => setting.isDisabled
    );

    return (
        <Modal
            actionButtons={renderActionButtons()}
            isOpen
            onClose={props.onCancel}
            size="large"
            title={translateKey('common.advancedSettings')}
        >
            {renderSettings()}
            {allSettingsAreDisabled && (
                <Alert appearance="warning">{translateKey('carePath.selectAtLeastOneProfileMessage')}</Alert>
            )}
        </Modal>
    );

    function renderSettings() {
        const settings = Object.entries(props.settings).map(
            ([key, values]: [string, TValueOfAddCarePathSettings[]]) => {
                return values.map((item: TValueOfAddCarePathSettings, index: number) => {
                    const isEmptyList = isEmptySettingsList(key as keyof IAddCarePathSettings, item);
                    const keyId = `${key}-${item.id}`;

                    return {
                        service: item.service,
                        component: (
                            <Fragment key={keyId}>
                                <FormSection
                                    id={keyId}
                                    key={item.id}
                                >
                                    <Box
                                        display="flex"
                                        mb={4}
                                    >
                                        <Heading size="medium">
                                            {getTitle(key as keyof IAddCarePathSettings, item.service?.name)}
                                        </Heading>
                                        {flattenedSettings.length > 1 && (
                                            <Switch
                                                ariaLabel="checkbox"
                                                checked={!item.isDisabled}
                                                onChange={() =>
                                                    props.onChange(
                                                        set(
                                                            props.settings,
                                                            `${key}[${index}].isDisabled`,
                                                            !item.isDisabled
                                                        ),
                                                        `${key}.${index}.isDisabled`
                                                    )
                                                }
                                                sx={{ ml: 4 }}
                                            />
                                        )}
                                    </Box>
                                    {isEmptyList && (
                                        <Text key={item.id}>{translateKey('episodeCategory.noAdvancedSettings')}</Text>
                                    )}
                                    {!item.isDisabled &&
                                        !isEmptyList &&
                                        renderItemList(item, key as keyof IAddCarePathSettings, index)}
                                </FormSection>
                            </Fragment>
                        ),
                    };
                });
            }
        );
        const settingsArray = flatten(settings);
        settingsArray.every((setting) => setting.service?.code.startsWith('OSAS'))
            ? settingsArray.sort(compareByServiceCodeSortOrder)
            : settingsArray.sort(compareByNestedServiceTypeSortOrderAndCode);

        return settingsArray.map((setting) => setting.component);
    }

    function renderActionButtons() {
        if (flattenedSettings.length === 1) {
            const key = findKey(props.settings, (setting) => setting.length > 0) as
                | keyof IAddCarePathSettings
                | undefined;
            if (key && isEmptySettingsList(key, props.settings[key][0] as TValueOfAddCarePathSettings)) {
                return [
                    <Button
                        onClick={props.onCancel}
                        variant="outline"
                        width="full-below-small"
                    >
                        {translateKey('common.close')}
                    </Button>,
                ];
            }
        }

        return [
            <Button
                onClick={props.onCancel}
                variant="outline"
            >
                {translateKey('common.cancel')}
            </Button>,
            <Button
                isDisabled={allSettingsAreDisabled}
                onClick={props.onSave}
            >
                {translateKey('common.save')}
            </Button>,
        ];
    }

    function renderItemList(item: TValueOfAddCarePathSettings, key: keyof IAddCarePathSettings, index: number) {
        const list = getSettingsList(key, item);
        const settingsToRender: {
            header: string;
            listKey: keyof TValueOfAddCarePathSettings;
            setting: IServiceEmploymentSetting;
        }[] = [];
        Object.keys(list).forEach((listKey) => {
            const typedListKey = listKey as keyof TValueOfAddCarePathSettings;
            const listConditionKey = list[typedListKey].conditionKey;
            if (listConditionKey && !item[listConditionKey as keyof TValueOfAddCarePathSettings]) {
                return;
            }
            if (
                list[typedListKey].conditionServiceCodes &&
                !list[typedListKey].conditionServiceCodes?.some(
                    (serviceCode: ServiceCode) => serviceCode === item.service.code
                )
            ) {
                return;
            }
            if (item.isAnalyst && (typedListKey === 'isBiometrician' || typedListKey === 'isGrader')) {
                return;
            }

            settingsToRender.push({
                listKey: typedListKey,
                setting: list[typedListKey],
                header: list[typedListKey].header,
            });
        });

        return chain(settingsToRender)
            .groupBy((i) => i.header)
            .map((setting, header) => {
                return (
                    <Fragment key={header}>
                        <FormSection title={header}>
                            {setting.map(({ listKey }) => {
                                const { updatePath, errorPath } = createUpdateAndErrorPath(key, index, listKey);
                                let itemValue = item[listKey];
                                if (key === 'serviceEmploymentSettings') {
                                    itemValue = getServiceEmploymentItemValue(listKey, item, 'enabled');
                                }

                                if (list[listKey].type === SettingAnswerTypes.subsequentServices) {
                                    return (
                                        <SubsequentServices
                                            allServiceConsumerSettings={props.settings.serviceConsumerSettings}
                                            currentServiceConsumerSettings={item}
                                            employmentStartDate={props.employmentStartDate}
                                            error={props.errors?.get(
                                                `${key}.${index}.autoSubsequentService.${listKey}`
                                            )}
                                            key={listKey}
                                            label={list[listKey].label}
                                            listKey={listKey}
                                            onChange={(value) =>
                                                props.onChange(set(props.settings, updatePath, value), errorPath)
                                            }
                                            onChangeSubsequentService={(value) =>
                                                props.onChange(
                                                    set(
                                                        props.settings,
                                                        `${key}[${index}].autoSubsequentService`,
                                                        value
                                                    ),
                                                    `${key}.${index}.autoSubsequentService.${listKey}`
                                                )
                                            }
                                            serviceType={item.service.serviceType}
                                        />
                                    );
                                }

                                return (
                                    <FormGroup
                                        key={item.id + listKey}
                                        label={list[listKey].label}
                                    >
                                        {list[listKey].type === SettingAnswerTypes.boolean && (
                                            <SettingsSwitch
                                                listKey={listKey}
                                                onChange={(value) =>
                                                    handleChangeSettings(value, updatePath, errorPath, listKey, index)
                                                }
                                                value={Boolean(itemValue)}
                                            />
                                        )}

                                        {list[listKey].type === SettingAnswerTypes.canReadAndOrWrite && (
                                            <SettingsCanWrite
                                                canWrite={
                                                    getServiceEmploymentItemValue(listKey, item, 'canWrite') as boolean
                                                }
                                                enabled={itemValue as boolean}
                                                error={props.errors.get(errorPath)}
                                                onChange={(canWrite, enabled) => {
                                                    handleChangeSettings(
                                                        canWrite,
                                                        `${updatePath.replace('.enabled', '.canWrite')}`,
                                                        errorPath,
                                                        listKey,
                                                        index
                                                    );
                                                    if (enabled !== (itemValue as boolean)) {
                                                        handleChangeSettings(
                                                            enabled,
                                                            updatePath,
                                                            errorPath,
                                                            listKey,
                                                            index
                                                        );
                                                    }
                                                }}
                                            />
                                        )}

                                        {list[listKey].type === SettingAnswerTypes.dropdown && (
                                            <DropdownInput
                                                error={props.errors?.get(errorPath)}
                                                onChange={(value) =>
                                                    handleChangeSettings(value, updatePath, errorPath, listKey, index)
                                                }
                                                placeholder={translateKey('common.clickAndChooseFromList')}
                                                selectOptions={generateOptions(list[listKey].options ?? [])}
                                                value={itemValue as string}
                                            />
                                        )}

                                        {list[listKey].type === SettingAnswerTypes.input && (
                                            <>
                                                <NumberInput
                                                    error={getValidationErrorTranslation(props.errors?.get(errorPath))}
                                                    label={translateKey('embloomQuestionnaires.weeks')}
                                                    onChange={(value) =>
                                                        handleChangeSettings(
                                                            value,
                                                            updatePath,
                                                            errorPath,
                                                            listKey,
                                                            index
                                                        )
                                                    }
                                                    value={itemValue as number}
                                                />
                                                <Box
                                                    alignSelf="center"
                                                    ml={2}
                                                >
                                                    <Text size="sm">{translateKey('embloomQuestionnaires.weeks')}</Text>
                                                </Box>
                                            </>
                                        )}
                                    </FormGroup>
                                );
                            })}
                        </FormSection>
                    </Fragment>
                );
            })
            .value();
    }

    function handleChangeSettings(
        value: boolean | string | number | null,
        updatePath: string,
        errorPath: string,
        listKey: keyof TValueOfAddCarePathSettings,
        index: number
    ) {
        if (listKey === 'isAnalyst') {
            const settings = { ...props.settings };
            settings.serviceProviderSettings[index] = {
                ...settings.serviceProviderSettings[index],
                isAnalyst: Boolean(value),
                isBiometrician: false,
                isGrader: false,
                isGraderSelector: false,
                isRequestCreator: false,
                isServiceConsumerSelector: false,
                canSelectCareProviderAsServiceConsumer: false,
            };
            props.onChange(settings, errorPath);
        } else if (listKey === 'isBiometrician') {
            const settings = { ...props.settings };
            settings.serviceProviderSettings[index] = {
                ...settings.serviceProviderSettings[index],
                isBiometrician: Boolean(value),
                isRequestCreator: false,
                isGraderSelector: false,
                isServiceConsumerSelector: false,
                canSelectCareProviderAsServiceConsumer: false,
            };
            props.onChange(settings, errorPath);
        } else if (listKey === 'isRequestCreator' && updatePath.startsWith('serviceProviderSettings')) {
            const settings = { ...props.settings };
            settings.serviceProviderSettings[index] = {
                ...settings.serviceProviderSettings[index],
                isRequestCreator: Boolean(value),
                isServiceConsumerSelector: false,
                canSelectCareProviderAsServiceConsumer: false,
            };
            props.onChange(settings, errorPath);
        } else if (listKey === 'isServiceConsumerSelector' && updatePath.startsWith('serviceProviderSettings')) {
            const settings = { ...props.settings };
            settings.serviceProviderSettings[index] = {
                ...settings.serviceProviderSettings[index],
                isServiceConsumerSelector: Boolean(value),
                canSelectCareProviderAsServiceConsumer: value
                    ? settings.serviceProviderSettings[index].canSelectCareProviderAsServiceConsumer
                    : false,
            };
            props.onChange(settings, errorPath);
        } else {
            props.onChange(set(props.settings, updatePath, value), errorPath);
        }
    }

    function getServiceEmploymentItemValue(
        listKey: keyof TValueOfAddCarePathSettings,
        item: TValueOfAddCarePathSettings,
        serviceActivityKey: keyof IAddCarePathServiceActivities
    ) {
        if (listKey.startsWith('activity_')) {
            const serviceActivityId = listKey.replace('activity_', '');
            const activityIndex = item.serviceActivities.findIndex((activity) => activity.id === serviceActivityId);

            return item.serviceActivities[activityIndex][serviceActivityKey];
        } else {
            return item.settings[listKey as keyof typeof settingDefinitionList];
        }
    }

    function createUpdateAndErrorPath(settingsTypeKey: keyof IAddCarePathSettings, index: number, updatedKey: string) {
        if (settingsTypeKey === 'serviceEmploymentSettings') {
            if (updatedKey.startsWith('activity_')) {
                const serviceActivityId = updatedKey.replace('activity_', '');
                const activityIndex = props.settings.serviceEmploymentSettings[index].serviceActivities.findIndex(
                    (activity) => activity.id === serviceActivityId
                );

                return {
                    updatePath: `${settingsTypeKey}[${index}].serviceActivities[${activityIndex}].enabled`,
                    errorPath: `${settingsTypeKey}.${index}.${updatedKey}`,
                };
            }

            return {
                updatePath: `${settingsTypeKey}[${index}].settings.${updatedKey}`,
                errorPath: `${settingsTypeKey}.${index}.${updatedKey}`,
            };
        }

        return {
            updatePath: `${settingsTypeKey}[${index}].${updatedKey}`,
            errorPath: `${settingsTypeKey}.${index}.${updatedKey}`,
        };
    }

    function getTitle(key: keyof IAddCarePathSettings, serviceName: string) {
        if (key === 'serviceReaderSettings') {
            return `${translateKey(`addCarePathSectionTitles.${key}`)} ${translateKey(
                `episodeCategoryEnum.${props.episodeCategory.name}`
            )}`;
        }
        if (key === 'serviceEmploymentSettings') {
            return serviceName;
        }

        return `${translateKey(`addCarePathSectionTitles.${key}`)} ${serviceName}`;
    }

    function isEmptySettingsList(key: keyof IAddCarePathSettings, item: TValueOfAddCarePathSettings) {
        if (key === 'serviceReaderSettings' || item.service.serviceType === ServiceType.contact) {
            return true;
        }
        if (key !== 'serviceConsumerSettings') {
            return false;
        }

        if (!possiblyEmptyServiceTypes.some((serviceType) => serviceType === item.service.serviceType)) {
            return false;
        }

        return !item.subsequentServices || item.subsequentServices.length === 0;
    }

    function getSettingsList(
        key: keyof IAddCarePathSettings,
        item: TValueOfAddCarePathSettings
    ): Record<string, IServiceEmploymentSetting> {
        const lists = settingsList();
        if (key.toLowerCase().includes('consumer')) {
            return lists[`consumer_${item.service.serviceType}`];
        }
        if (key.toLowerCase().includes('provider')) {
            return lists[`provider_${item.service.serviceType}`];
        }
        if (key.toLowerCase().includes('supervisor')) {
            return lists[`supervisor_${item.service.serviceType}`];
        }
        if (key.toLowerCase().includes('serviceemployments')) {
            return getAddCarePathServiceEmploymentSettingsList(item);
        }

        return {};
    }
}
